import React, { useEffect, useState } from "react";

import {
  GetReportDeploymentAccuracy,
  GetReportDeploymentAccuracyXlsx,
} from "../../api/report";
import { GetAcativeRestaurants } from "../../api/restaurant";

function getMonthStartDate() {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
}

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

function deviationClass(status) {
  if (status === "over") return "deployment-accuracy-over";
  if (status === "under") return "deployment-accuracy-under";
  return "";
}

function formatDayCellTitle(dayMetric) {
  return `План: ${dayMetric.plan_hours} ч, Факт: ${dayMetric.fact_hours} ч`;
}

const ReportDeploymentAccuracy = ({ token }) => {
  const [startDate, setStartDate] = useState(getMonthStartDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const [restaurantId, setRestaurantId] = useState(0);
  const [restaurantList, setRestaurantList] = useState([]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function loadRestaurants() {
      const restaurants = await GetAcativeRestaurants(token);
      if (Array.isArray(restaurants)) {
        setRestaurantList(restaurants);
      }
    }
    loadRestaurants();
  }, [token]);

  async function loadReport() {
    if (!startDate || !endDate) {
      setError("Укажите начало и конец периода");
      return;
    }
    if (startDate > endDate) {
      setError("Начало периода не может быть позже конца");
      return;
    }

    setLoading(true);
    setError("");
    const response = await GetReportDeploymentAccuracy(
      token,
      startDate,
      endDate,
      restaurantId,
    );
    setLoading(false);

    if (!response || !Array.isArray(response.restaurants) || !response.totals) {
      const detail = response?.detail;
      setError(typeof detail === "string" ? detail : "Не удалось загрузить отчёт");
      setReport(null);
      return;
    }
    setReport(response);
  }

  async function downloadReport() {
    if (!startDate || !endDate) {
      setError("Укажите начало и конец периода");
      return;
    }

    setError("");
    const response = await GetReportDeploymentAccuracyXlsx(
      token,
      startDate,
      endDate,
      restaurantId,
    );
    if (!response?.data) {
      setError("Не удалось скачать отчёт");
      return;
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const contentDisposition = response.headers["content-disposition"];
    let filename = "deployment_accuracy.xlsx";
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1].replace(/\.xlsx_?[^.]*$/, ".xlsx");
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  return (
    <div className="reports-page container">
      <h1>Точность попадания в деплоймент</h1>

      <div className="row g-3 align-items-end mb-4">
        <div className="col-md-3">
          <label className="form-label">Начало периода</label>
          <input
            className="form-control"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Конец периода</label>
          <input
            className="form-control"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Ресторан</label>
          <select
            className="form-select"
            value={restaurantId}
            onChange={(e) => setRestaurantId(Number(e.target.value))}
          >
            <option value={0}>Все рестораны</option>
            {restaurantList.map((restaurant) => (
              <option key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 d-grid gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={loadReport}
            disabled={loading}
          >
            {loading ? "Загрузка…" : "Показать"}
          </button>
          <button type="button" className="btn btn-success" onClick={downloadReport}>
            Скачать .xlsx
          </button>
        </div>
      </div>

      <div className="deployment-accuracy-legend card shadow-sm border-0 mb-4">
        <div className="card-body">
          <p className="mb-2">
            <strong>Формула отклонения:</strong> (Факт / План − 1) × 100%
          </p>
          <p className="mb-1">
            <strong>План</strong> — сумма часов смен на сайте за выбранный период.
          </p>
          <p className="mb-1">
            <strong>Факт</strong> — сумма отработанных часов по отметкам Boss Control
            за тот же период.
          </p>
          <p className="mb-0">
            <span className="deployment-accuracy-badge deployment-accuracy-over">
              зелёный
            </span>{" "}
            — факт больше плана;{" "}
            <span className="deployment-accuracy-badge deployment-accuracy-under">
              красный
            </span>{" "}
            — факт меньше плана.
          </p>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {report && (
        <>
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h2 className="h5 mb-3">Сводка за период</h2>
              <p className="text-muted mb-3">
                Период: {report.start_date} — {report.end_date}
              </p>
              <div className="table-responsive">
                <table className="table table-bordered deployment-accuracy-table mb-0">
                  <thead>
                    <tr>
                      <th>Ресторан</th>
                      <th className="text-center">План (ч)</th>
                      <th className="text-center">Факт (ч)</th>
                      <th className="text-center">Отклонение %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.restaurants.map((item) => (
                      <tr key={item.restaurant_id}>
                        <td>{item.restaurant_name}</td>
                        <td className="text-center">{item.plan_hours}</td>
                        <td className="text-center">{item.fact_hours}</td>
                        <td
                          className={`text-center fw-semibold ${deviationClass(item.status)}`}
                        >
                          {item.deviation_display}
                        </td>
                      </tr>
                    ))}
                    <tr className="fw-bold">
                      <td>{report.totals.restaurant_name}</td>
                      <td className="text-center">{report.totals.plan_hours}</td>
                      <td className="text-center">{report.totals.fact_hours}</td>
                      <td
                        className={`text-center ${deviationClass(report.totals.status)}`}
                      >
                        {report.totals.deviation_display}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h2 className="h5 mb-3">Разбивка по дням</h2>
              <p className="text-muted small mb-3">
                В ячейках — отклонение % за день. Наведите курсор, чтобы увидеть
                план и факт в часах.
              </p>
              <div className="table-responsive deployment-accuracy-daily-wrap">
                <table className="table table-bordered table-sm deployment-accuracy-daily-table mb-0">
                  <thead>
                    <tr>
                      <th className="deployment-accuracy-sticky-col">Ресторан</th>
                      {report.day_labels.map((label, index) => (
                        <th key={report.days[index]} className="text-center text-nowrap">
                          {label}
                        </th>
                      ))}
                      <th className="text-center text-nowrap deployment-accuracy-total-col">
                        Итого
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.restaurants.map((item) => (
                      <tr key={`daily-${item.restaurant_id}`}>
                        <td className="deployment-accuracy-sticky-col">
                          {item.restaurant_name}
                        </td>
                        {item.daily.map((dayMetric) => (
                          <td
                            key={`${item.restaurant_id}-${dayMetric.date}`}
                            className={`text-center ${deviationClass(dayMetric.status)}`}
                            title={formatDayCellTitle(dayMetric)}
                          >
                            {dayMetric.deviation_display}
                          </td>
                        ))}
                        <td
                          className={`text-center fw-semibold deployment-accuracy-total-col ${deviationClass(item.status)}`}
                          title={formatDayCellTitle(item)}
                        >
                          {item.deviation_display}
                        </td>
                      </tr>
                    ))}
                    <tr className="fw-bold">
                      <td className="deployment-accuracy-sticky-col">Итого</td>
                      {report.daily_totals.map((dayMetric) => (
                        <td
                          key={`total-${dayMetric.date}`}
                          className={`text-center ${deviationClass(dayMetric.status)}`}
                          title={formatDayCellTitle(dayMetric)}
                        >
                          {dayMetric.deviation_display}
                        </td>
                      ))}
                      <td
                        className={`text-center deployment-accuracy-total-col ${deviationClass(report.totals.status)}`}
                        title={formatDayCellTitle(report.totals)}
                      >
                        {report.totals.deviation_display}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportDeploymentAccuracy;
